import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import PageShell from '../layouts/PageShell'
import AppNavbar from '../features/AppNavbar'
import { reilmAPI } from '../services/apiClient'
import LoanCostsSection from '../features/flip/LoanCostsSection'
import AcquisitionCostsSection from '../features/flip/AcquisitionCostsSection'
import HoldingCostsSection from '../features/flip/HoldingCostsSection'
import SellingCostsSection from '../features/flip/SellingCostsSection'
import FlipPageHeader from '../features/flip/FlipPageHeader'
import FlipSummarySection from '../features/flip/FlipSummarySection'
import ScenarioRibbon, { SCENARIO_TITLE_MAX } from '../features/flip/ScenarioRibbon'
import MetricsStrip from '../features/flip/MetricsStrip'
import { DEFAULT_SCENARIO } from '../features/flip/defaults'
import { isLoanScenario } from '../features/flip/sectionNumbers'
import {
  SCENARIO_NUMBER_FIELDS,
  PAY_SCHED_MAX_SLOTS,
  parseScenarioField,
  parseScheduleSlot,
  normalizeScenarioNumbers,
} from '../features/flip/scenarioNumbers'
import {
  normalizeScenarioDates,
  placeholderPurchaseDate,
  withDerivedSaleDate,
  withPlaceholderPurchaseDate,
} from '../features/flip/scenarioDates'
import TemplatesFlow from '../features/flip/templates/TemplatesFlow'
import useTemplateOptions from '../features/flip/templates/useTemplateOptions'
import useFlipCompute from '../features/flip/useFlipCompute'
import { computedStillCurrent, dollarSeed, isDollarSwitch } from '../features/flip/percentDollarSeed'
import { buildComputePayload } from '../features/flip/computePayload'
import useScenarioAutosave from '../features/flip/useScenarioAutosave'
import { rememberScenario, takeReloadedScenarioId } from '../features/flip/rememberedScenario'
import {
  isHandledElsewhere,
  REJECTED_CHANGES_MESSAGE,
  ScenarioBusyError,
  SCENARIO_BUSY_MESSAGE,
  UnsavedChangesError,
} from '../features/flip/requestErrors'

// Stored scenarioType is a free string on older documents (spec §7.2): 'loan' in any case is a loan,
// anything else is cash, so the compute never receives null, '' or 'Loan'. The sale date always
// follows purchase date + Timeline months, whatever was stored.
const toScenario = (raw) => {
  const scenario = withDerivedSaleDate(normalizeScenarioDates(normalizeScenarioNumbers({ ...DEFAULT_SCENARIO, ...raw })))
  const loan = typeof scenario.scenarioType === 'string' && scenario.scenarioType.trim().toLowerCase() === 'loan'
  return { ...scenario, scenarioType: loan ? 'loan' : 'cash' }
}

const AUTOSAVE_ERROR_TEXT = {
  retrying: "We couldn't save your changes. Trying again…",
  failed: "We couldn't save your changes. Edit any field to try again.",
  gone: "We couldn't save your changes: this scenario no longer exists. Reload the page.",
}

// A compute 400 naming sales_date: the sale is more than 600 months after the purchase (reilm-be
// assertFlipAnalysisScenarioBody). Our text; no design draws it.
const SALE_TOO_FAR_TEXT = 'The sale date must be at most 600 months after the purchase date.'

// One page, many scenarios: every property.flipAnalysis element is a scenario, the chip ribbon picks
// the active one, and every section, the layout and the compute follow it. Create / rename /
// duplicate / delete are their own writes; field edits are autosaved (D13-D18) -- features/flip/
// README.md ("Scenarios", "Autosave").
export default function Flip() {
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [address, setAddress] = useState('')
  const [scenarios, setScenarios] = useState([])
  const [activeId, setActiveId] = useState(null)
  // What the page shows while there is no scenario yet (first visit, or the last one deleted): the
  // defaults behind "Start your analysis". Edits to it are local and dropped once Import creates one.
  const [placeholder, setPlaceholder] = useState(DEFAULT_SCENARIO)
  // Which pop-up is open: 'start' ("Start your analysis": no scenario yet, top template pre-checked),
  // 'manage' (Templates button: Manage Templates, letter 99 item 2) or null. Add Scenario is a
  // drop-down in the ribbon, not a pop-up (letter 106, R5a).
  const [templatesMode, setTemplatesMode] = useState(null)
  const [scenarioError, setScenarioError] = useState(null)
  // Write responses land after the user may have switched chips or run another write: they read the
  // list and the active id from here, never from the closure of the click that started them.
  const latest = useRef({ scenarios, activeId })
  latest.current = { scenarios, activeId }
  const writingRef = useRef(false)

  const active = scenarios.find((s) => s._id === activeId) ?? null
  const scenario = active ?? placeholder
  // Letter 104, response 2: a blank Purchase Date is calculated as today + 14. Only the compute and
  // the % -> $ check read `computeScenario`; the stored scenario (and so autosave) never carries it.
  const purchaseDatePlaceholder = placeholderPurchaseDate()
  const computeScenario = useMemo(
    () => withPlaceholderPurchaseDate(scenario, purchaseDatePlaceholder),
    [scenario, purchaseDatePlaceholder]
  )
  // Held off until the property has loaded, so the pre-load DEFAULT_SCENARIO is never sent.
  const {
    computed: lastComputed,
    computedId,
    computedType,
    computedInput: lastComputedInput,
    error: computeError,
    errorFields: computeErrorFields,
    pending: computePending,
    retry: retryCompute,
  } = useFlipCompute(id, computeScenario, {
    enabled: !loading && !loadError,
  })
  // Decision D9 (widened after review): figures computed for another scenario, or another type, are
  // never shown -- after a chip switch the derived values stay blank until this scenario's own compute
  // lands, and stay blank if it fails.
  const computedForOther =
    computedId !== (scenario._id ?? null) || isLoanScenario(computedType) !== isLoanScenario(scenario.scenarioType)
  const computed = lastComputed !== undefined && computedForOther ? undefined : lastComputed
  // `computed` is not for the inputs on screen: a computed fallback shown in an input waits for fresh values.
  const computeStale = computePending || Boolean(computeError)

  const ribbonScenarios = useMemo(() => scenarios.map((s) => ({ id: s._id, title: s.title })), [scenarios])
  // Add Scenario's checkmarks (letter 106, R5b): the templates a scenario here was made from. By id,
  // so a renamed template keeps its mark; a duplicate carries its source's template. Scenarios from
  // before templates stored it ('' activeTemplate) mark nothing.
  const addedTemplateIds = useMemo(() => new Set(scenarios.map((s) => s.activeTemplate).filter(Boolean)), [scenarios])
  const templateOptions = useTemplateOptions()

  // Only the server's stamp is taken from a save: the other fields on screen may be newer (D15).
  // Rename and autosave answer out of order, so the stamp only ever moves forward.
  const setModified = (scenarioId, modified) => {
    if (!modified) return
    setScenarios((prev) =>
      prev.map((s) => (s._id === scenarioId && !(Date.parse(s.modified) >= Date.parse(modified)) ? { ...s, modified } : s))
    )
  }
  const autosave = useScenarioAutosave(id, scenarios, {
    onSaved: (scenarioId, saved) => setModified(scenarioId, saved?.modified),
  })
  const { markSaved } = autosave
  // A scenario the server no longer has cannot be saved at all, whatever the other scenarios did.
  const autosaveError = active && autosave.goneIds.has(active._id) ? 'gone' : autosave.error

  useEffect(() => {
    let cancelled = false
    const reloadedId = takeReloadedScenarioId(id)
    reilmAPI
      .getProperty(id)
      .then(({ data }) => {
        if (cancelled) return
        const p = data.data.property
        setAddress([p.address?.street, p.address?.city, p.address?.state, p.address?.zip].filter(Boolean).join(', '))
        // Stored order is ribbon order; the first scenario opens, or after a browser reload of this
        // page the one that was active, if it still exists. An element without an _id (never
        // written by these routes) still gets a stable key so it can be shown and switched to.
        const loaded = (p.flipAnalysis ?? []).map((raw, i) => toScenario({ ...raw, _id: raw._id ?? `local-${i}` }))
        // An element without a server _id cannot be PATCHed, so it is never autosaved.
        loaded.forEach((s, i) => {
          if (p.flipAnalysis[i]._id) markSaved(s)
        })
        setScenarios(loaded)
        setActiveId((loaded.find((s) => s._id === reloadedId) ?? loaded[0])?._id ?? null)
        // First visit (mr-6 spec S15 decision 3): no scenario yet, so "Start your analysis" opens.
        if (loaded.length === 0) setTemplatesMode('start')
      })
      .catch((err) => {
        if (cancelled) return
        const status = err?.response?.status
        if (status === 401) return
        const code = err?.response?.data?.error?.code
        // 'locked' is its own value, not folded into 'load_failed': ExpiredModal (mounted at App
        // level) already states the cause and the only useful action, so this screen renders
        // nothing under it rather than a "try again" that cannot succeed -- same distinction
        // pages/PropertyDashboard.jsx makes for the identical 403.
        if (status === 403 && (code === 'TRIAL_EXPIRED' || code === 'SUBSCRIPTION_REQUIRED')) {
          setLoadError('locked')
          return
        }
        setLoadError('load_failed')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id, markSaved])

  // activeId is null while loading and after a failed load: writing then would erase the stored id.
  useEffect(() => {
    if (!loading && !loadError) rememberScenario(id, activeId)
  }, [id, activeId, loading, loadError])

  // Every field handler edits the active scenario only (or the placeholder while there is none).
  const updateActive = (update) => {
    if (activeId === null) setPlaceholder(update)
    else setScenarios((prev) => prev.map((s) => (s._id === activeId ? update(s) : s)))
  }

  // Inputs hand up typed text; numeric fields are stored as numbers (or null when blank) so the
  // formula core and the scenario validator never see a string -- features/flip/scenarioNumbers.js.
  const handleFieldChange = (field, value) => {
    const stored = SCENARIO_NUMBER_FIELDS.has(field) ? parseScenarioField(field, value) : value
    const derivesSaleDate = field === 'purchase_date' || field === 'holding_period'
    updateActive((prev) => (derivesSaleDate ? withDerivedSaleDate({ ...prev, [field]: stored }) : { ...prev, [field]: stored }))
  }

  // A switch lands on the scenario it was clicked on, even if it is applied after a chip switch.
  const applyToggle = useCallback((scenarioId, toggleKey, value, seed) => {
    const update = (prev) => ({
      ...prev,
      ...(seed ? { [seed.field]: seed.value } : {}),
      toggles: { ...(prev.toggles ?? {}), [toggleKey]: value },
    })
    if (scenarioId === null) setPlaceholder(update)
    else setScenarios((prev) => prev.map((s) => (s._id === scenarioId ? update(s) : s)))
  }, [])

  // % -> $ is applied only with the result of a compute of the inputs on screen: seeded with its
  // figure, or unseeded when that result has none for the row (a true dash). Until then the switch is
  // held; with nothing out, the compute is sent again. README.md ("Switching % -> $ keeps the figure").
  const queuedSwitchesRef = useRef([])
  const handleToggleChange = (toggleKey, value) => {
    const scenarioId = activeId
    queuedSwitchesRef.current = queuedSwitchesRef.current.filter((q) => !(q.scenarioId === scenarioId && q.toggleKey === toggleKey))
    if (!isDollarSwitch(toggleKey, scenario.toggles?.[toggleKey], value)) {
      applyToggle(scenarioId, toggleKey, value, null)
      return
    }
    if (computedStillCurrent(buildComputePayload(computeScenario), lastComputedInput, computed)) {
      applyToggle(scenarioId, toggleKey, value, dollarSeed(toggleKey, computed))
      return
    }
    if (!computePending) retryCompute()
    queuedSwitchesRef.current.push({ scenarioId, toggleKey })
  }

  // A held switch is dropped on a chip switch, and when the compute it waited for settles without a
  // result for the inputs on screen (failed, timed out, 401/403); the toggle then stays on %.
  useEffect(() => {
    queuedSwitchesRef.current = queuedSwitchesRef.current.filter((q) => q.scenarioId === activeId)
    if (computePending || queuedSwitchesRef.current.length === 0) return
    const queued = queuedSwitchesRef.current
    queuedSwitchesRef.current = []
    if (!computedStillCurrent(buildComputePayload(computeScenario), lastComputedInput, computed)) return
    for (const { scenarioId, toggleKey } of queued) applyToggle(scenarioId, toggleKey, '$', dollarSeed(toggleKey, computed))
  }, [computePending, computed, lastComputedInput, computeScenario, activeId, applyToggle])

  // Writes one paySchedProrations slot (ProjectedInterestPanel.jsx), leaving the others. The schedule
  // can outgrow the stored 24 slots; the gap is filled with 0 ("no override"), since a hole would be
  // sent as null and refused by the validator, as would a slot past its PAY_SCHED_MAX_SLOTS.
  const handleScheduleChange = (index, value) => {
    if (index >= PAY_SCHED_MAX_SLOTS) return
    updateActive((prev) => {
      const next = [...(prev.paySchedProrations ?? Array(24).fill(0))]
      while (next.length <= index) next.push(0)
      next[index] = parseScheduleSlot(value)
      return { ...prev, paySchedProrations: next }
    })
  }

  // One scenario write at a time, import included. A write asked for while another is running is
  // refused with a message and changes nothing; nothing is queued or dropped silently.
  const lockWrite = () => {
    if (writingRef.current) return false
    writingRef.current = true
    return true
  }
  const unlockWrite = () => {
    writingRef.current = false
  }

  // Nothing changes on screen until the server answers (rename's own undo aside), so a failed write
  // leaves the ribbon as it was, with a message.
  const runWrite = (request, onSuccess, failMessage, onFailure) => {
    if (!lockWrite()) {
      setScenarioError(SCENARIO_BUSY_MESSAGE)
      return
    }
    setScenarioError(null)
    request()
      .then(onSuccess)
      .catch((err) => {
        onFailure?.()
        if (!isHandledElsewhere(err)) setScenarioError(typeof failMessage === 'function' ? failMessage(err) : failMessage)
      })
      .finally(unlockWrite)
  }

  const setTitle = (scenarioId, title) =>
    setScenarios((prev) => prev.map((s) => (s._id === scenarioId ? { ...s, title } : s)))

  // Only the title and the stamp are taken from the response: other fields may hold unsaved edits.
  // The new name shows once the write is actually under way, never for a refused one.
  const handleRename = (scenarioId, title) => {
    const previous = latest.current.scenarios.find((s) => s._id === scenarioId)?.title
    runWrite(
      () => {
        setTitle(scenarioId, title)
        return reilmAPI.updateFlipScenario(id, scenarioId, { title })
      },
      ({ data }) => {
        const saved = data?.data?.scenario
        if (saved?.title) setTitle(scenarioId, saved.title)
        setModified(scenarioId, saved?.modified)
      },
      // A refused title will be refused again, so "try again" would be wrong there.
      (err) => {
        const error = err?.response?.data?.error
        return error?.code === 'VALIDATION_ERROR' && error?.details?.fields?.includes('title')
          ? `We couldn't rename the scenario: a name can be at most ${SCENARIO_TITLE_MAX} characters.`
          : "We couldn't rename the scenario. Please try again."
      },
      () => setTitle(scenarioId, previous)
    )
  }

  // The source is saved first, so the server's copy carries what is on screen (D13, D17). The copy
  // shows the source's values under the server's new id and name, right after its source (D7), and
  // becomes active; anything typed while the request ran is autosaved against the server's copy.
  const handleDuplicate = (scenarioId) => {
    runWrite(
      () => autosave.flush().then(() => reilmAPI.duplicateFlipScenario(id, scenarioId)),
      ({ data }) => {
        const saved = data?.data?.scenario
        if (!saved?._id) return
        markSaved(toScenario(saved))
        setScenarios((prev) => {
          const index = prev.findIndex((s) => s._id === scenarioId)
          if (index === -1) return [...prev, toScenario(saved)]
          const source = prev[index]
          const copy = {
            ...source,
            toggles: { ...(source.toggles ?? {}) },
            paySchedProrations: [...(source.paySchedProrations ?? Array(24).fill(0))],
            _id: saved._id,
            title: saved.title,
            created: saved.created,
            modified: saved.modified,
          }
          return [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)]
        })
        setActiveId(saved._id)
      },
      "We couldn't duplicate the scenario. Please try again."
    )
  }

  // Decision D6: deleting the active chip opens the previous one, else the next; deleting the last
  // one reopens "Start your analysis", as on a first visit. List and selection are updated from their
  // current values, so an edit or a chip switch made while the request ran is kept.
  const handleDelete = (scenarioId) => {
    runWrite(
      () => {
        autosave.suspend(scenarioId)
        return reilmAPI.deleteFlipScenario(id, scenarioId)
      },
      () => {
        autosave.forget(scenarioId)
        const list = latest.current.scenarios
        const index = list.findIndex((s) => s._id === scenarioId)
        if (index === -1) return
        const neighbour = list[index - 1] ?? list[index + 1] ?? null
        setScenarios((prev) => prev.filter((s) => s._id !== scenarioId))
        setActiveId((current) => (current === scenarioId ? neighbour?._id ?? null : current))
        if (list.length === 1) {
          setPlaceholder(DEFAULT_SCENARIO)
          setTemplatesMode('start')
        }
      },
      "We couldn't delete the scenario. Please try again.",
      () => autosave.resume(scenarioId)
    )
  }

  // Import ("Start your analysis", or one template from Add Scenario's drop-down) always adds, one
  // scenario per template, in list order (D1, D5); the first becomes active (D4). It takes the same
  // lock as the ribbon writes; the caller shows a rejection.
  // Template Save / New Template copy the server's version of the active scenario (D21, D22), so it
  // must hold what is on screen first.
  const saveActiveFirst = () =>
    autosave.flush().then(() => {
      const activeNow = latest.current.activeId
      if (!autosave.hasUnsaved(activeNow)) return
      throw new UnsavedChangesError(autosave.rejected(activeNow) ? REJECTED_CHANGES_MESSAGE : undefined)
    })

  const handleImport = (templateIds) => {
    if (!lockWrite()) return Promise.reject(new ScenarioBusyError())
    setScenarioError(null)
    return reilmAPI
      .createFlipScenarios(id, templateIds)
      .then(({ data }) => {
        const created = (data?.data?.scenarios ?? []).map(toScenario)
        if (created.length === 0) return
        created.forEach(markSaved)
        setScenarios((prev) => [...prev, ...created])
        setActiveId(created[0]._id)
      })
      .finally(unlockWrite)
  }

  // Add Scenario's drop-down adds on the click, with no pop-up to hold an error: the ribbon's own line
  // shows it. A checked template adds another copy, named by the server ("Cash (2)", letter 89).
  const handleAddFromTemplate = (templateId) => {
    handleImport([templateId]).catch((err) => {
      if (isHandledElsewhere(err)) return
      if (err instanceof ScenarioBusyError) return setScenarioError(err.message)
      const error = err?.response?.data?.error
      // No design for the limit (spec §3.3, D2): our own line, worded for one scenario.
      if (error?.code === 'FLIP_SCENARIO_LIMIT') setScenarioError('This property has the maximum number of scenarios. Delete one to add another.')
      // Deleted elsewhere since the list was loaded (another tab); the next open loads it afresh.
      else if (error?.code === 'TEMPLATE_NOT_FOUND') setScenarioError('This template no longer exists.')
      else setScenarioError("We couldn't add the scenario. Please try again.")
    })
  }

  if (loading) {
    return (
      <PageShell>
        <AppNavbar />
        <div className="flex items-center justify-center py-20 text-brand-muted text-[20px]">{'Loading…'}</div>
      </PageShell>
    )
  }

  if (loadError === 'locked') {
    return (
      <PageShell>
        <AppNavbar />
      </PageShell>
    )
  }

  if (loadError) {
    return (
      <PageShell>
        <AppNavbar />
        {/* brand.statusSale (#D85263) is the spec's own error colour (General Notes for Dev Team,
            verbatim: "If Net Loss appears, use D85263 red color") -- Tailwind's own red-400 is a
            different red. */}
        <div className="flex items-center justify-center py-20 text-brand-statusSale text-[14px]">
          Could not load this property, try again
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <AppNavbar />
      {/* One content column for header, strip and cards, capped at 998 (487 + 24 + 487, the 1440
          frame's column) and centred; two card columns from lg. README.md ("Page layout"). */}
      <div className="mx-auto w-full max-w-[1046px] px-[24px] py-[24px]">
        <div className="mb-[16px]">
          <FlipPageHeader
            propertyId={id}
            address={address}
            modified={scenario.modified}
            onOpenTemplates={() => setTemplatesMode('manage')}
            ribbon={
              <ScenarioRibbon
                scenarios={ribbonScenarios}
                activeId={activeId}
                onSelect={(next) => {
                  autosave.flush()
                  setActiveId(next)
                }}
                onAdd={handleAddFromTemplate}
                templateOptions={templateOptions}
                addedTemplateIds={addedTemplateIds}
                onRename={handleRename}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
              />
            }
          />
          {/* No drawn ribbon error state (spec §2.1): one line in the page's error red, like the
              compute error below. */}
          {scenarioError ? (
            <p role="alert" className="mt-[8px] text-right text-brand-statusSale text-[14px]">
              {scenarioError}
            </p>
          ) : null}
          {/* No drawn state for a failed autosave (D16): our own line; the values stay on screen. */}
          {autosaveError ? (
            <p role="status" className="mt-[8px] text-right text-brand-statusSale text-[14px]">
              {AUTOSAVE_ERROR_TEXT[autosaveError]}
            </p>
          ) : null}
        </div>
        {/* computed comes from POST /properties/:id/flip/compute via useFlipCompute (features/flip/
            README.md, "Live computed values"), Deal Score included. */}
        {/* MetricsStrip is a direct sibling of the sections below, not wrapped in its own div: its
            sticky child needs a containing block with real scrollable height to pin through, and a
            wrapper sized only to the strip's own height gives position: sticky 0px to travel in. */}
        <MetricsStrip computed={computed} />
        {computeError ? (
          // No drawn state for a failed recalculation (mr-8 spec covers only save errors): one line in
          // the spec's error red, last good values stay on screen -- README.md ("Live computed values").
          // Before any success there are no last values to point at, hence the second wording.
          <p role="status" className="mt-[8px] text-brand-statusSale text-[14px]">
            {computeErrorFields?.includes('sales_date')
              ? SALE_TOO_FAR_TEXT
              : computed === undefined
                ? "We couldn't calculate the results. Edit any field to try again."
                : "We couldn't update the calculations. Showing the last calculated values."}
          </p>
        ) : null}
        {/* Keyed by scenario: a chip switch starts every input, date draft and expanded panel afresh,
            so nothing typed on one scenario is left showing on another. */}
        <div key={scenario._id ?? 'placeholder'} className="flex flex-col gap-y-[24px] mt-[24px] lg:flex-row lg:items-start lg:gap-x-[24px]">
          <div className="flex flex-col gap-y-[24px] lg:flex-1 lg:min-w-0">
            {/* Cash scenarios have no loan, so no Loan Costs card (spec scenarios-cash-spec.28.09.md §4.1). */}
            {isLoanScenario(scenario.scenarioType) ? (
              <LoanCostsSection
                scenario={scenario}
                computed={computed}
                onFieldChange={handleFieldChange}
                onToggleChange={handleToggleChange}
                onScheduleChange={handleScheduleChange}
              />
            ) : null}
            <AcquisitionCostsSection
              scenario={scenario}
              computed={computed}
              computedInput={lastComputedInput}
              computeStale={computeStale}
              onFieldChange={handleFieldChange}
              onToggleChange={handleToggleChange}
            />
            <HoldingCostsSection
              scenario={scenario}
              computed={computed}
              onFieldChange={handleFieldChange}
              onToggleChange={handleToggleChange}
            />
            <SellingCostsSection
              scenario={scenario}
              computed={computed}
              computedInput={lastComputedInput}
              computeStale={computeStale}
              onFieldChange={handleFieldChange}
              onToggleChange={handleToggleChange}
            />
          </div>
          <div className="flex flex-col gap-y-[24px] lg:flex-1 lg:min-w-0">
            <FlipSummarySection
              scenario={scenario}
              purchaseDatePlaceholder={purchaseDatePlaceholder}
              computed={computed}
              onFieldChange={handleFieldChange}
              onToggleChange={handleToggleChange}
            />
            {/* Letter 104 (1.3): the Refinance Summary card is removed for now, its code kept for a later
                repurpose as a refinance exit -- features/flip/RefinanceSummarySection.jsx, README.md
                ("Refinance Summary"). Nothing else reads it: Sale Proceeds' Loan Payoff comes from
                compute (br4_est_payoff), not from the card. */}
          </div>
        </div>
      </div>
      <TemplatesFlow
        open={templatesMode !== null}
        preselectFirst={templatesMode === 'start'}
        manage={templatesMode === 'manage'}
        onClose={() => setTemplatesMode(null)}
        scenario={scenario}
        onImport={handleImport}
        saveSource={active && autosave.tracks(active._id) ? { propertyId: id, scenarioId: active._id } : null}
        onBeforeSave={saveActiveFirst}
      />
    </PageShell>
  )
}
